import React from "react";
import { Table, Button } from "react-bootstrap";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { format, addMonths, addWeeks, addYears } from "date-fns";
import logo from "../logo.png";
import ToPeso from "../components/misc/ToPeso";

// Utility function for rounding up
const roundUp = (amount) => {
  const amountString = amount.toFixed(2);
  const secondDecimalPlace = amountString.charAt(amountString.length - 1);

  if (secondDecimalPlace !== "0") {
    return parseFloat((amount + 0.01).toFixed(2));
  }

  return amount;
};

// Function to calculate due dates and amounts
const calculateDueDates = (startDate, interval, term, amount, interest) => {
  const dueDates = [];
  const principalAmount = amount / term;
  const interestAmountInitial = (amount * interest) / 100;
  const interestAmountMinusAdvance = (((amount * interest) / 100) * term) - interestAmountInitial;
  const interestAmount = interestAmountMinusAdvance / term;

  let currentDate = new Date(startDate);

  for (let i = 0; i < term; i++) {
    switch (interval.toLowerCase()) {
      case "monthly":
        currentDate = addMonths(currentDate, 1);
        break;
      case "bimonthly": // Twice a month, roughly every two weeks
        currentDate = addWeeks(currentDate, 2);
        break;
      case "weekly":
        currentDate = addWeeks(currentDate, 1);
        break;
      case "quarterly":
        currentDate = addMonths(currentDate, 3);
        break;
      case "semiannual":
        currentDate = addMonths(currentDate, 6);
        break;
      case "yearly":
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error("Invalid interval");
    }

    dueDates.push({
      amortizationNumber: i + 1,
      amortizationDate: format(currentDate, "MM/dd/yyyy"),
      principal: roundUp(principalAmount),
      interest: roundUp(interestAmount),
      total: roundUp(principalAmount + interestAmount),
    });
  }

  return dueDates;
};

// React Component
function DocumentAmortizationSchedule({ applicantDetails, loanProcessingDetails }) {
  if (!applicantDetails) {
    return null;
  }

  const generatePDF = async (loan) => {
    const schedule = calculateDueDates(
      loan.dateGranted,
      loan.loanInterval,
      loan.loanTerm,
      loan.amountGranted,
      loan.interestRate
    );

    const doc = new jsPDF();

    // Function to center text
    const centerText = (text, y, fontWeight = 'normal') => {
        // Set font style
         doc.setFont(undefined, fontWeight);

         // Get page width
         const pageWidth = doc.internal.pageSize.getWidth();
         // Calculate the text width
         const textWidth = doc.getTextDimensions(text).w;
         // Calculate the text's X coordinate to be centered
         const textX = (pageWidth - textWidth) / 2;
         // Add the text to the document
         doc.text(text, textX, y);
     }

    // Add company logo
    const logoImg = await fetch(logo);
    const logoBlob = await logoImg.blob();
    const logoURL = URL.createObjectURL(logoBlob);
    doc.addImage(logoURL, 'PNG', 10, 3, 60, 12.30); // Adjust position and dimensions as needed

    // Header
    doc.setFontSize(16);
    centerText("Amortization Schedule", 25, 'bold');

    // Borrower's Details
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Loan Number', 10, 35);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${loan.id.toString().padStart(6, '0') || "N/A"}`, 40, 35);

    doc.setFont(undefined, 'bold');
    doc.text("Borrower's Name", 10, 40);
    doc.setFont(undefined, 'normal');
    doc.text(
      `: ${applicantDetails.firstName.toUpperCase()} ${applicantDetails.middleName.toUpperCase() || ""} ${applicantDetails.lastName.toUpperCase()}`,
      40,
      40
    );

    doc.setFont(undefined, 'bold');
    doc.text("Address", 10, 45);
    doc.setFont(undefined, 'normal');
    doc.text(
      `: ${applicantDetails.street.toUpperCase()}, ${applicantDetails.barangay.toUpperCase()},`,
      40,
      45
    );
    doc.text(
        `${applicantDetails.municipality.toUpperCase()}, ${applicantDetails.province.toUpperCase()} ${applicantDetails.zipCode.toUpperCase()}`,
        42,
        50
      );

      console.log(loan);

    // Loan Details
    doc.text('....................................................', 10, 60);
    doc.setFont(undefined, 'bold');
    doc.text('Date Granted', 10, 65);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${format(new Date(loan.dateGranted), "MM/dd/yyyy")}`, 40, 65);

    doc.setFont(undefined, 'bold');
    doc.text('Principal', 10, 70);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${loan.amountGranted.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, 40, 70);

    doc.setFont(undefined, 'bold');
    doc.text('Interest', 10, 75);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${((loan.amountGranted * (loan.interestRate / 100)) * loan.loanTerm).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`, 40, 75);


    doc.text('.....................................', 70, 60);
    doc.setFont(undefined, 'bold');
    doc.text('Term', 70, 65);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${loan.loanTerm}`, 95, 65);

    doc.setFont(undefined, 'bold');
    doc.text('Interest Rate', 70, 70);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${loan.interestRate}%`, 95, 70);

    doc.setFont(undefined, 'bold');
    doc.text('Interest Type', 70, 75);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${loan.paymentType.toUpperCase()}`, 95, 75);


    doc.text('...............................................................', 120, 60);
    doc.setFont(undefined, 'bold');
    doc.text('Loan Ledger', 120, 65);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${loan.loanLedger.toUpperCase()}`, 145, 65);

    doc.setFont(undefined, 'bold');
    doc.text('Loan Interval', 120, 70);
    doc.setFont(undefined, 'normal');
    doc.text(`: ${loan.loanInterval.toUpperCase()}`, 145, 70);

    schedule.push({
        amortizationNumber: 'Totals:',
        amortizationDate: '',
        principal: loan.amountGranted,
        interest: ((loan.amountGranted * (loan.interestRate / 100)) * loan.loanTerm) - (loan.amountGranted * (loan.interestRate / 100)),
        total: (loan.amountGranted + ((loan.amountGranted * (loan.interestRate / 100)) * loan.loanTerm)) - (loan.amountGranted * (loan.interestRate / 100)),
      });
    

    // Amortization Table
    const tableData = schedule.map((row) => [
      row.amortizationNumber,
      row.amortizationDate,
      row.principal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      row.interest.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      row.total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
    ]);

    doc.autoTable({
      startY: 90,
      head: [["Amortization #", "Due Date", "Principal", "Interest", "Total"]],
      body: tableData,
      theme: "grid",
    });

    // Save PDF
    doc.save(`Amortization_Schedule_${applicantDetails.firstName.toUpperCase()}_${applicantDetails.lastName.toUpperCase()}_${loan.id}.pdf`);
  };

  return (
    <>
      <h3>Applicant Details</h3>
      <Table className="list-container" hover responsive>
        <tbody>
          <tr>
            <td><strong>Full Name</strong></td>
            <td>{`${applicantDetails.firstName} ${applicantDetails.middleName || ""} ${applicantDetails.lastName}`}</td>
          </tr>
          <tr>
            <td><strong>Address</strong></td>
            <td>{`${applicantDetails.street}, ${applicantDetails.barangay}, ${applicantDetails.municipality}, ${applicantDetails.province}`}</td>
          </tr>
        </tbody>
      </Table>

      <h3>Loan Processing Details</h3>
      {loanProcessingDetails.length > 0 ? (
        <Table className="list-container" hover responsive>
          <thead>
            <tr>
              <th>Type</th>
              <th>Amount Granted</th>
              <th>Date Granted</th>
              <th>Interest Rate</th>
              <th>Loan Term</th>
              <th>Payment Type</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loanProcessingDetails.map((loan, index) => (
              <tr key={index}>
                <td>{loan.loanProcessingType}</td>
                <td><ToPeso amount={loan.amountGranted.toFixed(2)} /></td>
                <td>{format(new Date(loan.dateGranted), "MM/dd/yyyy")}</td>
                <td>{loan.interestRate}%</td>
                <td>{loan.loanTerm} months</td>
                <td>{loan.paymentType}</td>
                <td>
                  <Button variant="primary" onClick={() => generatePDF(loan)}>
                    Download Schedule
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <p>No loan processing details found for this applicant.</p>
      )}
    </>
  );
}

export default DocumentAmortizationSchedule;
